import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Phone, Trash2, Edit2, X, User, Mail, Check, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactsPage = ({ user }) => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [calling, setCalling] = useState(null);
  const [inviting, setInviting] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.get(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('kaplie_token');

    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact updated!');
      } else {
        await axios.post(`${API}/contacts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact added!');
      }
      
      setShowAddModal(false);
      setEditingContact(null);
      setFormData({ name: '', phone: '', email: '' });
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    
    try {
      const token = localStorage.getItem('kaplie_token');
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleCall = async (contact) => {
    setCalling(contact.id);
    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.post(`${API}/calls/initiate`, 
        { contact_id: contact.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`SMS sent to ${contact.name}!`);
      navigate(`/call/${response.data.channel_name}`);
    } catch (error) {
      toast.error('Failed to initiate call');
    } finally {
      setCalling(null);
    }
  };

  const handleQuickInvite = async (contact) => {
    if (!contact.email) {
      toast.error(`${contact.name} has no email on file. Edit the contact to add one.`);
      return;
    }
    setInviting(contact.id);
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.post(
        `${API}/contacts/${contact.id}/quick-invite`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email invite sent to ${contact.name}!`);
      // Optimistic update with returned timestamp + channel
      setContacts((prev) => prev.map((c) => c.id === contact.id ? {
        ...c,
        last_invited_at: res.data.last_invited_at,
        last_invite_channel: res.data.channel_name,
      } : c));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(null);
    }
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setFormData({ name: contact.name, phone: contact.phone, email: contact.email || '' });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingContact(null);
    setFormData({ name: '', phone: '', email: '' });
  };

  return (
    <div className="min-h-screen bg-black/40 text-white">
      {/* Header */}
      <nav className="border-b border-white/[0.08] bg-white/[0.03]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <button className="p-2 hover:bg-[#1E1E24] rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
            </Link>
            <h1 className="text-xl font-bold">Contacts</h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="add-contact-button"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full font-semibold hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all"
          >
            <Plus className="w-4 h-4" />
            Add Contact
          </button>
        </div>
      </nav>

      {/* Contacts List */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center text-[#A1A1AA] py-20">Loading contacts...</div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-20">
            <User className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-[#A1A1AA] mb-4">No contacts yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full font-semibold hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all"
            >
              Add your first contact
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {contacts.map((contact) => {
              const invitedLabel = formatRelativeTime(contact.last_invited_at);
              return (
              <div
                key={contact.id}
                data-testid={`contact-${contact.id}`}
                className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 flex items-center justify-between hover:border-white/[0.12] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/[0.06] rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-[#D4AF37]">
                      {contact.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{contact.name}</p>
                      {invitedLabel && (
                        <button
                          type="button"
                          onClick={() => contact.last_invite_channel && navigate(`/call/${contact.last_invite_channel}`)}
                          disabled={!contact.last_invite_channel}
                          data-testid={`invited-badge-${contact.id}`}
                          title={contact.last_invite_channel ? 'Hop into the same call you invited them to' : ''}
                          className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all disabled:opacity-60 disabled:cursor-default"
                        >
                          <Check className="w-2.5 h-2.5" />
                          <span>Invited {invitedLabel}</span>
                          {contact.last_invite_channel && (
                            <span className="ml-1 inline-flex items-center gap-0.5 pl-1 border-l border-[#D4AF37]/30 group-hover:text-white transition-colors">
                              Join <ArrowRight className="w-2.5 h-2.5 group-hover:translate-x-0.5 transition-transform" />
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-[#A1A1AA]">{contact.phone}</p>
                    {contact.email && (
                      <p className="text-xs text-zinc-600 mt-0.5">{contact.email}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleQuickInvite(contact)}
                    disabled={inviting === contact.id || !contact.email}
                    data-testid={`quick-invite-${contact.id}`}
                    title={contact.email ? 'Send email invite with call link' : 'Add an email to enable Quick Invite'}
                    className="p-3 bg-gradient-to-br from-[#D4AF37] to-[#C5A059] rounded-full hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {inviting === contact.id ? (
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    ) : (
                      <Mail className="w-5 h-5 text-black" />
                    )}
                  </button>
                  <button
                    onClick={() => handleCall(contact)}
                    disabled={calling === contact.id}
                    data-testid={`call-contact-${contact.id}`}
                    title="Call now (sends SMS)"
                    className="p-3 bg-[#22C55E] rounded-full hover:bg-[#16A34A] transition-all disabled:opacity-50"
                  >
                    <Phone className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={() => openEditModal(contact)}
                    className="p-3 bg-white/[0.06] rounded-full hover:bg-white/[0.1] transition-all"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(contact.id)}
                    className="p-3 bg-white/[0.06] rounded-full hover:bg-[#FF3366] transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-4">
          <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {editingContact ? 'Edit Contact' : 'Add Contact'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-white/[0.06] rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="contact-name-input"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-white focus:border-[#D4AF37]/50 focus:outline-none"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="contact-phone-input"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-white focus:border-[#D4AF37]/50 focus:outline-none"
                  placeholder="+1 234 567 8900"
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Email (optional)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contact-email-input"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-white focus:border-[#D4AF37]/50 focus:outline-none"
                  placeholder="john@example.com"
                />
              </div>
              
              <button
                type="submit"
                data-testid="save-contact-button"
                className="w-full py-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full font-semibold hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all"
              >
                {editingContact ? 'Save Changes' : 'Add Contact'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
